import { useState, useEffect } from 'react';

export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type PWAPortalType = 'customer' | 'admin' | 'staff';

export function detectPortalFromUrl(urlPath?: string, search?: string, hash?: string): PWAPortalType {
  const p = (urlPath || (typeof window !== 'undefined' ? window.location.pathname : '')).toLowerCase();
  const s = (search || (typeof window !== 'undefined' ? window.location.search : '')).toLowerCase();
  const h = (hash || (typeof window !== 'undefined' ? window.location.hash : '')).toLowerCase();

  if (
    p.includes('/super-admin') ||
    p.includes('/admin') ||
    h.includes('super-admin') ||
    h.includes('admin') ||
    s.includes('app=admin') ||
    s.includes('portal=admin') ||
    s.includes('source=pwa&app=admin')
  ) {
    return 'admin';
  }

  if (
    p.includes('/staff') ||
    p.includes('/partner') ||
    h.includes('staff') ||
    h.includes('partner') ||
    s.includes('app=staff') ||
    s.includes('portal=staff') ||
    s.includes('source=pwa&app=staff')
  ) {
    return 'staff';
  }

  return 'customer';
}

let globalDeferredPrompt: BeforeInstallPromptEvent | null = null;
let promptPortal: PWAPortalType = typeof window !== 'undefined' ? detectPortalFromUrl() : 'customer';
const promptListeners = new Set<(prompt: BeforeInstallPromptEvent | null, portal: PWAPortalType) => void>();

export const PWA_PROMPT_CHANGED_EVENT = 'blishka-pwa-prompt-changed';

// Detect standalone mode
export function isStandaloneApp(): boolean {
  if (typeof window === 'undefined') return false;
  return Boolean(
    window.matchMedia('(display-mode: standalone)').matches ||
    window.matchMedia('(display-mode: window-controls-overlay)').matches ||
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

// Detect client platform
export function getDevicePlatform() {
  if (typeof window === 'undefined') {
    return {
      isIOS: false,
      isAndroid: false,
      isSamsung: false,
      isDesktop: false,
      isMac: false,
      isWindows: false
    };
  }

  const ua = window.navigator.userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const isAndroid = /android/.test(ua);
  const isSamsung = /samsungbrowser/.test(ua);
  const isMac = /macintosh|mac os x/.test(ua) && !isIOS;
  const isWindows = /windows nt/.test(ua);
  const isDesktop = !isIOS && !isAndroid;

  return {
    isIOS,
    isAndroid,
    isSamsung,
    isDesktop,
    isMac,
    isWindows
  };
}

// Register global listener immediately
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e: Event) => {
    e.preventDefault();
    globalDeferredPrompt = e as BeforeInstallPromptEvent;
    promptPortal = detectPortalFromUrl();
    promptListeners.forEach((fn) => fn(globalDeferredPrompt, promptPortal));
    window.dispatchEvent(
      new CustomEvent(PWA_PROMPT_CHANGED_EVENT, {
        detail: { prompt: globalDeferredPrompt, portal: promptPortal }
      })
    );
  });

  window.addEventListener('appinstalled', () => {
    globalDeferredPrompt = null;
    promptListeners.forEach((fn) => fn(null, promptPortal));
    window.dispatchEvent(new CustomEvent(PWA_PROMPT_CHANGED_EVENT, { detail: null }));
  });
}

export function getGlobalDeferredPrompt(): BeforeInstallPromptEvent | null {
  return globalDeferredPrompt;
}

export function getActivePromptPortal(): PWAPortalType {
  return promptPortal;
}

/**
 * Triggers native installation dialog.
 * If targetPortal is specified, prevents executing a customer prompt when
 * an admin or staff portal install was explicitly intended.
 */
export async function triggerNativeInstall(
  targetPortal?: PWAPortalType
): Promise<'accepted' | 'dismissed' | 'unavailable' | 'portal_mismatch'> {
  if (!globalDeferredPrompt) {
    return 'unavailable';
  }

  // Safety guard: NEVER trigger an install prompt for the customer app if the caller specifically requested 'admin' or 'staff'!
  if (targetPortal && promptPortal !== targetPortal) {
    console.warn(
      `[PWA] Prevented prompt execution mismatch: captured prompt is for '${promptPortal}', but '${targetPortal}' was requested.`
    );
    return 'portal_mismatch';
  }

  try {
    await globalDeferredPrompt.prompt();
    const { outcome } = await globalDeferredPrompt.userChoice;
    if (outcome === 'accepted') {
      globalDeferredPrompt = null;
      promptListeners.forEach((fn) => fn(null, promptPortal));
      window.dispatchEvent(new CustomEvent(PWA_PROMPT_CHANGED_EVENT, { detail: null }));
    }
    return outcome;
  } catch (err) {
    console.warn('PWA prompt execution error:', err);
    return 'unavailable';
  }
}

/**
 * React hook to access and trigger PWA installation with portal awareness
 */
export function usePWAInstall() {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(getGlobalDeferredPrompt);
  const [portal, setPortal] = useState<PWAPortalType>(getActivePromptPortal);
  const [isStandalone, setIsStandalone] = useState<boolean>(isStandaloneApp);
  const [platform] = useState(getDevicePlatform);

  useEffect(() => {
    const handlePromptChange = (
      updatedPrompt: BeforeInstallPromptEvent | null,
      updatedPortal: PWAPortalType
    ) => {
      setPrompt(updatedPrompt);
      setPortal(updatedPortal);
    };

    promptListeners.add(handlePromptChange);

    const handleCustomEvent = (e: any) => {
      if (e.detail) {
        setPrompt(e.detail.prompt || null);
        setPortal(e.detail.portal || detectPortalFromUrl());
      } else {
        setPrompt(null);
      }
    };
    window.addEventListener(PWA_PROMPT_CHANGED_EVENT, handleCustomEvent);

    // Re-check standalone mode
    const standaloneCheck = () => {
      setIsStandalone(isStandaloneApp());
    };
    window.addEventListener('resize', standaloneCheck);

    return () => {
      promptListeners.delete(handlePromptChange);
      window.removeEventListener(PWA_PROMPT_CHANGED_EVENT, handleCustomEvent);
      window.removeEventListener('resize', standaloneCheck);
    };
  }, []);

  return {
    prompt,
    hasNativePrompt: Boolean(prompt),
    promptPortal: portal,
    isAdminPromptReady: Boolean(prompt && portal === 'admin'),
    isStaffPromptReady: Boolean(prompt && portal === 'staff'),
    isCustomerPromptReady: Boolean(prompt && portal === 'customer'),
    isStandalone,
    platform,
    triggerInstall: (targetPortal?: PWAPortalType) => triggerNativeInstall(targetPortal)
  };
}
